// ============================================================================
// Odds screen -> tracker.
//
// Turns a clicked odds cell into the OPPORTUNITY shape the EV page's
// TrackBetModal already takes, so the modal, the stake calculator and the save
// path are reused rather than reimplemented. Nothing here writes to the
// database; it only builds the object handed to useTrackBet().
//
// The whole job is recovering the structured settlement fields (side, point,
// player_name, team_name, base_market_type) from an odds outcome. The EV
// engines emit those directly; the odds API does not, because it is describing
// a market rather than a bet. Everything needed is present, but three families
// need deriving rather than copying, and each fails SILENTLY if you don't:
// the bet saves, looks right in the tracker, and then never settles.
//
// Verified against public-api.py's outcome builder, which is the authority on
// which fields it does and does not send.
// ============================================================================

import { SELECTION_SHORT_NAMES } from '../config/markets'

// Markets whose selection is a TEAM rather than an over/under. These are the
// ones where `name` carries the team, and they are also the two the settler
// treats as margin markets.
const MARGIN_MARKETS = ['head_to_head', 'alternate_lines']

// The period suffixes a market key can carry. Stripped only to ask "what family
// is this", never to build base_market_type — settlement wants the window kept.
const WINDOW_SUFFIXES = ['1st_half', '1st_5_innings', '1st_1_innings', '1st_period', '1st_set']

/** The market family, with any period window removed. */
function familyOf(marketKey) {
  let key = String(marketKey ?? '')
  for (const suffix of WINDOW_SUFFIXES) {
    if (key.endsWith(`_${suffix}`)) {
      key = key.slice(0, -(suffix.length + 1))
      break
    }
  }
  return key
}

const isMargin = (marketKey) => MARGIN_MARKETS.includes(familyOf(marketKey))

/**
 * Which side of an over/under this outcome is, or null when the market has no
 * sides at all.
 *
 * MILESTONE MARKETS ARE THE TRAP. Their outcomes are named for the threshold
 * ("20+"), not "Over", so reading the name gives a side the settler cannot use
 * and every milestone bet stays pending. The EV engine has always hardcoded
 * "Over" for these (ev_engine_shared_clean.py, the is_milestone branch) and
 * this has to agree with it, because both write into the same bet_data shape
 * and are read back by the same settler.
 *
 * The line itself needs no adjustment: milestone points arrive as .5 values on
 * the same lines as the standard market, so "20+" is already Over 19.5.
 */
function sideOf(marketKey, outcome) {
  if (String(marketKey ?? '').endsWith('_milestones')) return 'Over'
  const name = outcome?.name
  if (name === 'Over' || name === 'Under') return name
  return null
}

/**
 * The team this selection backs, or null if it doesn't back one.
 *
 * public-api.py only attaches `team_name` for team-total markets and
 * alternate_lines, AND only when the outcome's own name differs from the team:
 *
 *     if market_key in team_markets or market_key == "alternate_lines":
 *         if team_name and outcome.get("name") != team_name:
 *             outcome_data["team_name"] = team_name
 *
 * So head_to_head NEVER carries it (h2h is not in team_markets) and a handicap
 * drops it whenever the outcome is already named after the team. In both cases
 * the team is sitting in `name`. Copying the field straight across leaves
 * team_name null, which makes settlement's margin path fail to find the backed
 * team, so every h2h and handicap bet tracked here would sit pending forever
 * while player props settled normally — a market bug, seemingly.
 *
 * Soccer's "Draw" outcome resolves to a team matching neither side, so it
 * returns null from the settler and stays manual. Correct: there is no team.
 */
function teamOf(marketKey, outcome) {
  // On a MARGIN market the backed team is `name`, and it is checked FIRST.
  // team_name is only a fallback here because it cannot be trusted on these
  // markets: TAB prices Asian handicaps with the OPPONENT in team_name —
  //     { name: "Philadelphia Phillies", point: -1.5, team_name: "Atlanta Braves" }
  // and taking team_name first stored the opposing team on the bet, which makes
  // settlement resolve the handicap against the wrong side and record a win as a
  // loss. `name` is the team the user actually clicked, on every book.
  if (isMargin(marketKey) && outcome?.name) return outcome.name
  if (outcome?.team_name) return outcome.team_name
  return null
}

/**
 * The display sentence for a selection.
 *
 * Names the STAT on a player prop, which the odds table's own label does not:
 * there the market comes from a dropdown above the table, so every row shares
 * it and "Over 0.5" is unambiguous in context. Lifted out of that context — in
 * a betslip holding two different markets, or in the tracker's Selection
 * column — it no longer says 0.5 of what.
 *
 * Same convention the EV pages use (formatSelection + SELECTION_SHORT_NAMES),
 * so a prop reads the same however it was tracked. The milestone key is tried
 * first and the base second, since the map spells some markets only one way:
 * `player_goals` is in it, `player_goals_milestones` is not.
 */
function selectionOf(marketKey, outcome) {
  const point = outcome?.point
  const name = outcome?.name

  if (outcome?.player_name) {
    // familyOf strips PERIOD windows only, so the milestone suffix has to come
    // off separately for the lookup — the map spells `player_goals` but not
    // `player_goals_milestones`.
    const base = familyOf(marketKey).replace('_milestones', '')
    const stat = SELECTION_SHORT_NAMES[marketKey] ?? SELECTION_SHORT_NAMES[base] ?? ''

    // The SIDE, not the outcome's own name. A milestone outcome is named for
    // its threshold ("1+"), which beside the .5 line reads as "1+ 0.5" and
    // contradicts the Over this actually settles as. The EV engine writes these
    // as "Over 0.5", and both land in the same tracker column.
    const side = sideOf(marketKey, outcome) ?? name
    const line = point != null ? ` ${Math.abs(point)}` : ''
    return `${outcome.player_name} — ${side}${line}${stat ? ` ${stat}` : ''}`
  }
  // Yes/No at a round number - the generic branch below would say "Yes +2".
  if (marketKey === 'fight_to_start_round' && point != null) {
    return `Start Round ${point} — ${name}`
  }
  if (point != null) {
    const sign = point > 0 && name !== 'Over' && name !== 'Under' ? '+' : ''
    // Same distrust of team_name as teamOf(): on a margin market it can hold the
    // opposing team (TAB), which would read as "Atlanta Braves — Philadelphia
    // Phillies -1.5". The name already carries the team on those markets.
    const qualifier = outcome?.team_name && !isMargin(marketKey) ? `${outcome.team_name} — ` : ''
    return `${qualifier}${name} ${sign}${point}`
  }
  // Method of Victory / Round Betting / winning-margin bands repeat the name
  // and are told apart only by `description` - without it a tracked
  // "Volkanovski — KO/TKO" bet read as a plain Volkanovski win.
  if (outcome?.description) return `${name} — ${outcome.description}`
  return String(name ?? '')
}

/**
 * One odds cell as an opportunity.
 *
 * @param outcome     the clicked outcome ({ name, price, point?, player_name?, team_name? })
 * @param marketKey   that outcome's market key, period suffix included
 * @param game        the odds page's game entry ({ game: { home_team, ... } })
 * @param bookmaker   display name of the book the price came from
 * @param sport       the page's selected sport id (AFL, NRL, ...)
 *
 * Returns null when there is no price to track, so a caller can guard on it.
 */
export function oppFromOutcome({ outcome, marketKey, game, bookmaker, sport, gameUrl = null, price = null }) {
  // The PRICE OF THE CELL CLICKED, not the outcome's own.
  //
  // A row is keyed by selection, so every bookmaker offering it writes over the
  // same entry and the outcome kept is simply the last one seen. Reading the
  // price off it therefore meant reading some other book's number — usually
  // wrong, and null whenever that last outcome had no usable price at all,
  // which is what made AFL handicaps unclickable while NRL's worked: the same
  // selection there is priced by far more books across far more lines.
  //
  // Falls back to the outcome only when no cell price was supplied.
  const odds = Number(price ?? outcome?.price)
  if (!outcome || !Number.isFinite(odds) || odds <= 1) return null

  const envelope = game?.game ?? game
  const homeTeam = envelope?.home_team ?? null
  const awayTeam = envelope?.away_team ?? null

  return {
    sport,
    // settlementFields splits this back apart on " vs ", so the separator is a
    // contract with settlement.js, not a display choice.
    //
    // FUTURES have no fixture. home_team carries the MARKET name and there is
    // no away team, so the usual join would produce '' and the bet would land
    // in the tracker with a blank match. The market name is what identifies it
    // here — "Brownlow Medal Winner" plus the selection "Nick Daicos" says the
    // whole bet.
    //
    // A market name contains no " vs ", so settlementFields puts it in
    // home_team and leaves away_team null. That is deliberate: with a null
    // away_team and a null commence_time, auto-settle can never match this bet
    // to a game row, so it stays pending until settled BY HAND. There is no
    // results feed for the Brownlow or the Premiership, and settling those
    // automatically is not worth building for two markets a year.
    match: envelope?.futuresTitle
      || (homeTeam && awayTeam ? `${homeTeam} vs ${awayTeam}` : ''),
    // Null for futures — a season-long market has no start time. The tracker
    // renders it as N/A rather than a date.
    commence_time: envelope?.commence_time ?? null,

    market_type: marketKey,
    // The _milestones variant settles off the BASE stat field, which the results
    // feed names after it. Same strip the engines do.
    base_market_type: String(marketKey ?? '').replace('_milestones', ''),

    selection: selectionOf(marketKey, outcome),
    side: sideOf(marketKey, outcome),
    point: outcome?.point ?? null,
    player_name: outcome?.player_name ?? null,
    team_name: teamOf(marketKey, outcome),

    bookmaker,
    // That book's own page for this fixture. The API sends one games[] entry
    // PER BOOKMAKER, so the url on the entry belongs to the book in it — the
    // odds page carries it down onto the book when it merges those entries.
    // Display only, and dropped by legRecord when a multi is saved.
    game_url: gameUrl,
    // The modal offers a book dropdown; a single-entry list pins it to the one
    // actually clicked, since that price belongs to that book alone.
    bookmakers: [bookmaker],
    odds,
    // The modal's odds field reads `original_odds` (the EV engines' name for the
    // price actually on offer), so it is sent under both names.
    original_odds: odds,

    // The odds screen is not an EV product and has no edge to quote. Null rather
    // than 0: zero is a claim that the bet is exactly fair, and it would be
    // averaged into the tracker's theoretical-EV figure as though it were one.
    ev_percentage: null,
    bookmaker_count: null,

    // Provenance. Kept distinct from 'single' so these are filterable and so the
    // EV stats can tell a bet that never had an edge from one that did.
    bet_type_tag: 'odds_screen'
  }
}

/**
 * Whether a tracked bet from this market can ever auto-settle.
 *
 * Only correct_score is excluded, and only because the settler has no resolver
 * for it. Used to warn at track time rather than letting the bet sit pending
 * with no explanation.
 */
export function willAutoSettle(marketKey) {
  const family = familyOf(marketKey)
  return family !== 'correct_score' && family !== 'correct_score_sets'
}
